import { DropDownMenu, type DropDownOption } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import { Component, createMemo, createSignal } from 'solid-js';

import type { CurrentWorkspaceBarProps } from './CurrentWorkspaceBar.types';
import { fxWorkspaceDropdownSlotClass } from './fx-workspace-dropdown-slot-class';

// DropdownOptions renders the active check after the label and the Create New
// icon before it. Every row reserves a left gutter so labels stay aligned while
// both icons sit in that gutter.
const workspaceDropdownPaneClass =
  '!translate-y-0 mt-1 [&_li]:relative [&_li]:!pl-10 [&_li.active_i]:absolute [&_li.active_i]:left-[0.7rem] [&_li_.icon-left]:absolute [&_li_.icon-left]:left-[0.7rem] [&_hr]:!my-1';

const CREATE_WORKSPACE_OPTION_INDEX = 0;

export const CurrentWorkspaceBar: Component<CurrentWorkspaceBarProps> = (
  props
) => {
  const [isOpen, setIsOpen] = createSignal(false);

  const dropDownOptions = createMemo((): DropDownOption[] => [
    {
      text: t('mobileMapping.fxWorkspace.createNew'),
      icon: 'tc-icon-content-add',
      testId: 'create-workspace-option',
    },
    ...props.workspaceOptions.map((option) => ({
      text: option.label,
      value: option.key,
      testId: `workspace-option-${option.key}`,
    })),
  ]);

  const activeWorkspaceLabel = createMemo(
    () =>
      props.workspaceOptions.find(
        (option) => option.key === props.activeWorkspaceId
      )?.label ?? '---'
  );

  const handleSelectionChanged = (_text: string, index: number): void => {
    if (index === CREATE_WORKSPACE_OPTION_INDEX) {
      props.onCreateWorkspace();
      return;
    }

    const workspace = props.workspaceOptions[index - 1];
    // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
    if (workspace !== undefined) {
      props.onActiveWorkspaceChange(workspace.key);
    }
  };

  return (
    <div
      class="flex shrink-0 items-center border-b border-base-200 px-3 py-3"
      data-testid="current-workspace-bar"
    >
      <div
        class={fxWorkspaceDropdownSlotClass}
        data-testid="active-workspace-dropdown"
      >
        <DropDownMenu
          type="button"
          anchorText={activeWorkspaceLabel()}
          anchorIcon={
            isOpen() ? 'tc-icon-arrow-drop-up' : 'tc-icon-arrow-drop-down'
          }
          anchorClassName="button p-0 text-sm font-semibold min-h-[inherit]"
          dropDownOptions={dropDownOptions()}
          dropDownClassName={workspaceDropdownPaneClass}
          optionsType="both"
          addSeparators={[CREATE_WORKSPACE_OPTION_INDEX]}
          selected={props.activeWorkspaceId}
          showOnScroll={true}
          onMenuOpen={setIsOpen}
          onSelectionChanged={handleSelectionChanged}
        />
      </div>
    </div>
  );
};
